import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ProductModal = ({ isOpen, onClose, product, whatsappNumber }) => {
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [currentImg, setCurrentImg] = useState(0);
  const [zoomPos, setZoomPos] = useState({ x: 0, y: 0 });
  const [isZoomActive, setIsZoomActive] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);

    if (isOpen) {
      document.body.style.overflow = 'hidden'; 
    } else {
      document.body.style.overflow = 'unset';
    }

    if (product) {
      setSelectedVariant(product.variants?.[0]);
      setCurrentImg(0);
      setIsZoomActive(false);
    }

    return () => {
      window.removeEventListener('resize', checkMobile);
      document.body.style.overflow = 'unset';
    };
  }, [product, isOpen]);

  if (!isOpen || !product) return null;

  const handleZoomToggle = () => {
    if (!selectedVariant || !imgRef.current) return;
    setIsZoomActive(!isZoomActive);
    if (!isZoomActive) setZoomPos({ x: 50, y: 50 });
  };

  const handleZoomMove = (e) => {
    if (!isZoomActive || !selectedVariant || !imgRef.current) return;
    const { left, top, width, height } = imgRef.current.getBoundingClientRect();
    let x = e.clientX ?? e.touches?.[0]?.clientX;
    let y = e.clientY ?? e.touches?.[0]?.clientY;
    const mouseX = ((x - left) / width) * 100;
    const mouseY = ((y - top) / height) * 100;
    setZoomPos({ x: Math.max(0, Math.min(100, mouseX)), y: Math.max(0, Math.min(100, mouseY)) });
  };

  const handleOrder = () => {
    const message = `Hola Plásticos Diana, me interesa cotizar: ${product.name} (Opción: ${selectedVariant?.color || 'Única'}).`;
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const images = selectedVariant?.images || ['https://via.placeholder.com/600'];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 md:p-6 lg:p-10">
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="absolute inset-0 bg-white/90 backdrop-blur-xl"
        onClick={onClose}
      />

      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 30 }}
        className="relative bg-white border border-gray-100 w-full max-w-7xl h-full max-h-[92vh] rounded-[2rem] md:rounded-[3rem] shadow-2xl flex flex-col lg:flex-row overflow-hidden"
      >

        <button 
          onClick={onClose} 
          className="absolute top-5 right-5 z-[160] w-10 h-10 flex items-center justify-center bg-[#a6192e] text-white rounded-full shadow-lg hover:scale-110 active:scale-95 transition-all"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="w-full lg:w-[60%] flex flex-col h-[50vh] lg:h-full bg-gray-50/50 border-b lg:border-b-0 lg:border-r border-gray-100 overflow-hidden">
          <div
            ref={imgRef}
            className={`relative flex-1 w-full min-h-0 overflow-hidden bg-white group ${isZoomActive ? 'cursor-zoom-out' : 'cursor-zoom-in'}`}
            onClick={handleZoomToggle}
            onMouseMove={!isMobile ? handleZoomMove : undefined}
            onTouchMove={isMobile ? handleZoomMove : undefined}
          >
            <motion.img
              animate={{
                scale: isZoomActive ? (isMobile ? 2.5 : 2) : 1,
                transformOrigin: `${zoomPos.x}% ${zoomPos.y}%`
              }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              src={images[currentImg]}
              alt="Insumo"
              className="w-full h-full object-contain p-6"
            />
          </div>

          {images.length > 1 && (
            <div className="p-4 bg-white border-t border-gray-100 shrink-0">
              <div className="flex gap-3 overflow-x-auto pb-2 custom-scrollbar snap-x">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={(e) => { e.stopPropagation(); setCurrentImg(i); setIsZoomActive(false); }}
                    className={`relative shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden border-2 transition-all snap-start ${currentImg === i ? 'border-[#a6192e] shadow-md scale-105' : 'border-transparent opacity-50'}`}
                  >
                    <img src={img} className="w-full h-full object-cover" alt="" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="w-full lg:w-[40%] flex flex-col h-1/2 lg:h-full bg-white overflow-hidden">
          <div className="flex-1 overflow-y-auto p-8 md:p-12 custom-scrollbar">
            <div className="mb-8">
              <span className="text-[#a6192e] font-black text-[10px] tracking-[0.4em] uppercase italic mb-2 block">Ficha de Insumo</span>
              <h2 className="text-4xl md:text-5xl font-black text-slate-900 uppercase tracking-tighter leading-none mb-4">{product.name}</h2>
              {product.categories?.includes("Novedad") && (
                <span className="inline-block bg-[#a6192e] text-white text-[8px] font-black px-3 py-1 rounded-full uppercase italic tracking-widest shadow-lg shadow-red-200">NUEVO</span>
              )}
            </div>

            <div className="space-y-8">
              <div>
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic border-b border-gray-100 pb-2 mb-4">Descripción</h4>
                <p className="text-slate-500 text-sm italic leading-relaxed">{product.description || "Insumo técnico de alta calidad seleccionado por Plásticos Diana."}</p>
              </div>

              {product.variants?.length > 1 && (
                <div>
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic border-b border-gray-100 pb-2 mb-4">Modelos / Colores</h4>
                  <div className="flex flex-wrap gap-2">
                    {product.variants.map((v, i) => (
                      <button
                        key={i}
                        onClick={() => { setSelectedVariant(v); setCurrentImg(0); setIsZoomActive(false); }}
                        className={`px-4 py-2 rounded-xl border text-[9px] font-black transition-all uppercase italic ${selectedVariant?.color === v.color ? 'bg-[#a6192e] border-[#a6192e] text-white shadow-lg' : 'bg-gray-50 border-gray-200 text-slate-400 hover:border-gray-300'}`}
                      >
                        {v.color}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="p-8 bg-gray-50/50 border-t border-gray-100">
            <button
              onClick={handleOrder}
              className="w-full py-5 bg-[#a6192e] text-white font-black rounded-2xl shadow-2xl shadow-red-200 hover:bg-[#8b1526] transition-all active:scale-95 flex items-center justify-center gap-3 uppercase text-xs tracking-widest"
            >
              COTIZAR ESTE INSUMO
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.148-.67-1.614-.917-2.214-.242-.584-.487-.504-.67-.514-.173-.009-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" /></svg>
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ProductModal;